import * as d3 from "d3";
import { useAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
    AlertingData,
    EmissionRate,
} from "../../../../../../apiClient/generated";
import {
    useResizeObserver,
    useZoomOnPlot,
} from "../../../../../../ui/Plot/hooks";
import { selectionContextAtom } from "../../../state";
import { computePlotTicks, TIMELINE_MARGINS } from "../plot";

// Modified tableau10 colors for stable emission color palette
const EMISSION_COLORS = [
    "#6194AA",
    "#315466",
    "#486B2E",
    "#869F52",
    "#EC8536",
    "#D85052",
    "#A47095",
    "#FB959F",
    "#E9C24F",
    "#906A57",
];

/**
 * resampleForSum
 *
 * Create a new dataset that is composed of 60 minute intervals,
 * interpolating data for the plots and using zero outside the interest area.
 * The output here will be the same format as the input, but with resampled values
 * and matching timestamps, and all plots will have the same amount of entries.
 *
 * @param data: AlertingData[]
 * @returns result: AlertingData[]
 */
const resampleForSum = (data: AlertingData[]) => {
    // Find start and end times on all alerting data.
    const [startTime, endTime] = d3.extent(
        data.flatMap((dataSeries) => dataSeries.rates),
        (rate: EmissionRate) => rate.timestamp,
    ) as [Date, Date];

    if (!startTime || !endTime) {
        return [];
    }

    const interval = 60 * 60 * 1000;
    const tolerance = 20 * 60 * 1000;

    // Generate time points from start to end
    const timePoints: Date[] = [];
    for (let t = startTime.getTime(); t <= endTime.getTime(); t += interval) {
        timePoints.push(new Date(t));
    }

    // Resample each data series
    return data.map((dataSeries) => {
        const sortedRates = [...dataSeries.rates].sort(
            (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
        );

        // A series without any rates contributes zero everywhere
        if (sortedRates.length === 0) {
            return {
                ...dataSeries,
                rates: timePoints.map((timePoint) => ({
                    timestamp: timePoint,
                    measurement: 0,
                })),
            };
        }

        // Find the data range this series covers
        const seriesStart = sortedRates[0].timestamp.getTime();
        const seriesEnd =
            sortedRates[sortedRates.length - 1].timestamp.getTime();

        const resampledRates: EmissionRate[] = timePoints.map((timePoint) => {
            const time = timePoint.getTime();

            // If outside the series range, use zero
            if (time < seriesStart || time > seriesEnd) {
                return { timestamp: timePoint, measurement: 0 };
            }

            // Check for exact match within tolerance
            const exactMatch = sortedRates.find(
                (rate) =>
                    Math.abs(rate.timestamp.getTime() - time) <= tolerance,
            );
            if (exactMatch) {
                return {
                    timestamp: timePoint,
                    measurement: exactMatch.measurement,
                };
            }

            // Find surrounding points for interpolation
            let i = 0;
            while (
                i < sortedRates.length &&
                sortedRates[i].timestamp.getTime() < time
            ) {
                i++;
            }

            // Interpolate between points
            if (i === 0) {
                return {
                    timestamp: timePoint,
                    measurement: sortedRates[0].measurement,
                };
            }
            if (i >= sortedRates.length) {
                return {
                    timestamp: timePoint,
                    measurement:
                        sortedRates[sortedRates.length - 1].measurement,
                };
            }

            const before = sortedRates[i - 1];
            const after = sortedRates[i];
            const ratio =
                (time - before.timestamp.getTime()) /
                (after.timestamp.getTime() - before.timestamp.getTime());
            const interpolated =
                before.measurement +
                ratio * (after.measurement - before.measurement);

            return { timestamp: timePoint, measurement: interpolated };
        });

        return {
            ...dataSeries,
            rates: resampledRates,
        };
    });
};

interface SensirionAlertingPlotProps {
    data: AlertingData[];
    dataDateRange: {
        dateBefore: Date;
        dateAfter: Date;
    };
    sumPlots?: boolean;
    setHoverContext?: (data?: { x: Date; y: number }) => void;
    clickContext?: { x: Date; y: number };
    setClickContext?: (data?: { x: Date; y: number }) => void;
}

type PlotPoint = { date: Date; value: number; windDirection: number };
type StackRow = { date: Date } & Record<string, number>;

export const SensirionAlertingPlot = ({
    data,
    dataDateRange,
    setHoverContext,
    clickContext,
    setClickContext,
    sumPlots,
}: SensirionAlertingPlotProps) => {
    const svgRef = useRef();
    const containerRef = useRef<HTMLDivElement>();
    const [, setCursorPosition] = useState<{
        x: number;
        y: number;
    } | null>(null);
    const dimensions = useResizeObserver(containerRef);

    // Selection context
    const [selectionContext, setSelectionContext] =
        useAtom(selectionContextAtom);

    // Zoom controls
    const { zoomDomain, addZoomBrushHandle, resetZoom, currentTransform } =
        useZoomOnPlot({
            dimensions,
            svgRef,
            disableDoubleClickReset: true,
        });
    useEffect(() => {
        if (zoomDomain.x) {
            setSelectionContext((d) => ({
                ...d,
                dateRange: {
                    start: zoomDomain.x[0] as Date,
                    end: zoomDomain.x[1] as Date,
                },
            }));
        } else {
            setSelectionContext((d) => ({ ...d, dateRange: undefined }));
        }
    }, [zoomDomain, setSelectionContext]);

    // Filter data
    const processedData = useMemo(() => {
        if (sumPlots) {
            return resampleForSum(data);
        }
        return data;
    }, [data, sumPlots]);

    useEffect(() => {
        const margin = {
            ...TIMELINE_MARGINS,
            bottom: 12,
            top: 12,
        };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Create stable color scale based on emission IDs
        // Sort emission IDs to ensure consistent color assignment
        const emissionIds = [...processedData]
            .map((d) => d.emissionId)
            .sort((a, b) => String(a).localeCompare(String(b)));
        const colorScale = d3
            .scaleOrdinal<string | number>()
            .domain(emissionIds)
            .range(EMISSION_COLORS);

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        // Create SVG
        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        // Create the chart group
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        chart
            .append("rect")
            .attr("width", width)
            .attr("height", height)
            .attr("fill", "transparent");

        // Clip lines at chart boundaries
        const clipId = `sensirion-clip-${Math.random().toString(36).substr(2, 9)}`;
        svg.append("defs")
            .append("clipPath")
            .attr("id", clipId)
            .append("rect")
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", width)
            .attr("height", height);

        // X Axis
        const dateRange = [dataDateRange.dateAfter, dataDateRange.dateBefore];
        const xScale = d3.scaleUtc().domain(dateRange).range([0, width]);

        const { tickFormat, tickValues } = computePlotTicks({
            startDate: dataDateRange.dateAfter,
            endDate: dataDateRange.dateBefore,
            plotWidth: width,
        });

        // X Axis with guaranteed start/end ticks
        chart
            .append("g")
            .attr("class", "x-axis-full")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues(tickValues)
                    .tickSize(5)
                    .tickSizeOuter(0)
                    .tickPadding(2)
                    .tickFormat(tickFormat),
            )
            .selectAll("text")
            .attr("font-size", "12px")
            .attr("fill", "#777");

        // Add grid lines for x-axis
        chart
            .append("g")
            .attr("class", "grid")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues(tickValues)
                    .tickSize(-height + margin.top + margin.bottom)
                    .tickSizeOuter(1)
                    .tickFormat(() => ""),
            )
            .selectAll("line")
            .style("stroke", "#e5e7eb")
            .style("stroke-dasharray", "6,6");

        // Y Axis
        let yDomainMin = 0;
        let yDomainMax: number;

        // Calculate the max of stacked values using original data (not processedData)
        // This keeps the y-axis stable when toggling sum mode
        const allTimestamps = new Set<number>();
        data.forEach((emission) => {
            emission.rates.forEach((rate) => {
                if (rate.timestamp && Number.isFinite(rate.measurement)) {
                    allTimestamps.add(rate.timestamp.getTime());
                }
            });
        });

        yDomainMax = d3.max(Array.from(allTimestamps), (ts) => {
            let sum = 0;
            data.forEach((emission) => {
                const match = emission.rates.find(
                    (r) => r.timestamp.getTime() === ts,
                );
                if (match && Number.isFinite(match.measurement)) {
                    sum += match.measurement;
                }
            });
            return sum;
        });

        // Apply zoom domain if it exists
        if (zoomDomain.y) {
            yDomainMin = zoomDomain.y[0];
            yDomainMax = zoomDomain.y[1];
        }

        const y = d3
            .scaleLinear()
            .domain([yDomainMin, yDomainMax])
            .nice()
            .range([height - margin.bottom, margin.top]);

        const yGrid = d3
            .axisLeft(y)
            .tickValues(tickValues)
            .tickSize(-width)
            .tickFormat(() => "");

        // Add grid lines for y-axis
        chart
            .append("g")
            .attr("class", "grid")
            .call(yGrid)
            .call((g) => g.select(".domain").remove())
            .selectAll("line")
            .style("stroke", "#e5e7eb")
            .style("stroke-width", 1)
            .style("stroke-dasharray", "6,6");

        chart
            .append("g")
            .classed("", true)
            .call(d3.axisLeft(y))
            .call((g) => g.select(".domain").remove())
            .selectAll("text")
            .style("font-size", "14px")
            .attr("text-anchor", "center")
            .attr("dx", -3)
            .attr("dy", (d, i, arr) => {
                if (i === 0) {
                    return 0;
                } else if (i === arr.length - 1) {
                    return 12;
                }
                return 5;
            });

        // Add y-axis label
        svg.append("text")
            .classed("text-gray-500", true)
            .attr("transform", `rotate(-90) translate(${-height / 2}, 15)`)
            .attr("x", 0)
            .attr("y", 0)
            .attr("text-anchor", "middle")
            .style("font-size", "14px")
            .text("kg/hr (methane)");

        // --- draw lines and points (with clip path) ---
        const plotGroup = chart
            .append("g")
            .attr("clip-path", `url(#${clipId})`);

        if (sumPlots) {
            // Stacked area chart
            // Sort emissions from smallest to largest based on total emissions
            const sortedProcessedData = [...processedData].sort((a, b) => {
                const totalA = d3.sum(a.rates, (r) =>
                    Number.isFinite(r.measurement) ? r.measurement : 0,
                );
                const totalB = d3.sum(b.rates, (r) =>
                    Number.isFinite(r.measurement) ? r.measurement : 0,
                );
                return totalA - totalB;
            });

            // Collect all unique timestamps
            const allTimestamps = new Set<number>();
            sortedProcessedData.forEach((emission) => {
                emission.rates.forEach((rate) => {
                    if (rate.timestamp && Number.isFinite(rate.measurement)) {
                        allTimestamps.add(rate.timestamp.getTime());
                    }
                });
            });

            const sortedTimestamps = Array.from(allTimestamps).sort(
                (a, b) => a - b,
            );

            // Collect stack data
            const stackData = sortedTimestamps.map((ts) => {
                const row = { date: new Date(ts) } as StackRow;
                sortedProcessedData.forEach((emission) => {
                    // Find exact timestamp match
                    const match = emission.rates.find(
                        (r) => r.timestamp.getTime() === ts,
                    );
                    row[`emission_${emission.emissionId}`] = match
                        ? match.measurement
                        : 0;
                });
                return row;
            });

            // Handle stack data: d3 stack and area generator
            const seriesKeys = sortedProcessedData.map(
                (d) => `emission_${d.emissionId}`,
            );
            const stack = d3
                .stack<StackRow>()
                .keys(seriesKeys)
                .order(d3.stackOrderNone)
                .offset(d3.stackOffsetNone);
            const series = stack(stackData);
            const area = d3
                .area<d3.SeriesPoint<StackRow>>()
                .x((d) => xScale(d.data.date))
                .y0((d) => y(d[0]))
                .y1((d) => y(d[1]));

            // Draw layers
            series.forEach((layer, i) => {
                const emissionId = sortedProcessedData[i].emissionId;
                const color = colorScale(emissionId);
                plotGroup
                    .append("path")
                    .datum(layer)
                    .attr("fill", color)
                    .attr("fill-opacity", 0.4)
                    .attr("stroke", color)
                    .attr("stroke-width", 1)
                    .attr("stroke-opacity", 1)
                    .attr("d", area);
            });
        } else {
            const datasets = processedData.map((d) => ({
                name: d.emissionId,
                values: d.rates.map((m) => ({
                    date: new Date(m.timestamp),
                    value: m.measurement,
                })) as PlotPoint[],
            }));

            // --- line generator ---
            const line = d3
                .line<PlotPoint>()
                .defined((d) => Number.isFinite(d.value))
                .x((d) => xScale(d.date))
                .y((d) => y(d.value));

            datasets.forEach((dataset, i) => {
                const emissionId = processedData[i].emissionId;
                const color = colorScale(emissionId);

                // Draw line connecting points
                plotGroup
                    .append("path")
                    .datum(dataset.values)
                    .attr("class", `series-line-${i}`)
                    .attr("fill", "none")
                    .attr("stroke", color)
                    .attr("opacity", 1)
                    .attr("stroke-width", 1)
                    .attr("d", line);

                // Draw points on top of line
                plotGroup
                    .selectAll(`.series-points-${i}`)
                    .data(
                        dataset.values.filter((d) => Number.isFinite(d.value)),
                    )
                    .join("circle")
                    .attr("class", `series-points-${i}`)
                    .attr("cx", (d) => xScale(d.date))
                    .attr("cy", (d) => y(d.value))
                    .attr("r", 3)
                    .attr("fill", color)
                    .attr("stroke", "white")
                    .attr("stroke-width", 1);
            });
        }

        // Sensirion 24h data selection hover
        const oneDayWidth =
            xScale(d3.timeDay.offset(dateRange[0], 1)) - xScale(dateRange[0]);

        // Hover marker group with left and right shadows
        const hoverMarker = chart.append("g").style("pointer-events", "none");

        const hoverLeftShadow = hoverMarker
            .append("rect")
            .attr("x", 0)
            .attr("y", margin.top)
            .attr("width", 0)
            .attr("height", height - margin.bottom - margin.top)
            .attr("fill", "rgba(100,100,100,0.08)");

        const hoverRightShadow = hoverMarker
            .append("rect")
            .attr("x", 0)
            .attr("y", margin.top)
            .attr("width", 0)
            .attr("height", height - margin.bottom - margin.top)
            .attr("fill", "rgba(100,100,100,0.08)");

        // Click context marker
        const clickMarker = chart.append("g").style("pointer-events", "none");

        // Left shadow rectangle (everything before the selection)
        clickMarker
            .append("rect")
            .attr("x", 0)
            .attr("y", margin.top)
            .attr(
                "width",
                clickContext?.x ? xScale(clickContext?.x) - oneDayWidth / 2 : 0,
            )
            .attr("height", height - margin.bottom - margin.top)
            .attr(
                "fill",
                clickContext ? "rgba(100,100,100,0.1)" : "transparent",
            );

        // Right shadow rectangle (everything after the selection)
        clickMarker
            .append("rect")
            .attr(
                "x",
                clickContext?.x ? xScale(clickContext?.x) + oneDayWidth / 2 : 0,
            )
            .attr("y", margin.top)
            .attr(
                "width",
                clickContext?.x
                    ? width - (xScale(clickContext?.x) + oneDayWidth / 2)
                    : 0,
            )
            .attr("height", height - margin.bottom - margin.top)
            .attr(
                "fill",
                clickContext ? "rgba(100,100,100,0.1)" : "transparent",
            );

        // Left dotted border
        clickMarker
            .append("line")
            .attr(
                "x1",
                clickContext?.x
                    ? xScale(clickContext?.x) - oneDayWidth / 2
                    : -1000,
            )
            .attr("y1", margin.top)
            .attr(
                "x2",
                clickContext?.x
                    ? xScale(clickContext?.x) - oneDayWidth / 2
                    : -1000,
            )
            .attr("y2", height - margin.bottom)
            .attr("stroke", clickContext ? "#666" : "transparent")
            .attr("stroke-width", 1)
            .attr("opacity", 0.6)
            .attr("stroke-dasharray", "4,4");

        // Right dotted border
        clickMarker
            .append("line")
            .attr(
                "x1",
                clickContext?.x
                    ? xScale(clickContext?.x) + oneDayWidth / 2
                    : -1000,
            )
            .attr("y1", margin.top)
            .attr(
                "x2",
                clickContext?.x
                    ? xScale(clickContext?.x) + oneDayWidth / 2
                    : -1000,
            )
            .attr("y2", height - margin.bottom)
            .attr("stroke", clickContext ? "#666" : "transparent")
            .attr("stroke-width", 1)
            .attr("opacity", 0.6)
            .attr("stroke-dasharray", "4,4");

        // Hover events
        const containerRect = containerRef.current.getBoundingClientRect();
        // Snap date to nearest 6-hour UTC boundary (0, 6, 12, 18)
        const snapTo12Hours = (date: Date): Date => {
            const snapped = new Date(date);
            const hour = snapped.getUTCHours();
            const snappedHour = Math.round(hour / 12) * 12;
            if (snappedHour === 24) {
                snapped.setUTCDate(snapped.getUTCDate() + 1);
                snapped.setUTCHours(0, 0, 0, 0);
            } else {
                snapped.setUTCHours(snappedHour, 0, 0, 0);
            }
            return snapped;
        };

        chart
            .on("mousemove", (event) => {
                const rawX = xScale.invert(
                    event.clientX - containerRect.left - margin.left,
                );
                const snappedX = snapTo12Hours(rawX);

                // Update hover shadows to show inverted selection preview
                const hoverLeft = xScale(snappedX) - oneDayWidth / 2;
                const hoverRight = xScale(snappedX) + oneDayWidth / 2;

                hoverLeftShadow.attr("width", Math.max(0, hoverLeft));
                hoverRightShadow
                    .attr("x", hoverRight)
                    .attr("width", Math.max(0, width - hoverRight));

                if (setHoverContext) {
                    setHoverContext({
                        x: rawX,
                        y: y.invert(
                            event.clientY - containerRect.top - margin.top,
                        ),
                    });
                }
            })
            .on("mouseout", () => {
                // Hide hover shadows
                hoverLeftShadow.attr("width", 0);
                hoverRightShadow.attr("width", 0);
                if (setHoverContext) {
                    setHoverContext(undefined);
                }
            })
            .on("click", (event) => {
                if (setClickContext) {
                    const rawX = xScale.invert(
                        event.clientX - containerRect.left - margin.left,
                    );
                    setClickContext({
                        x: snapTo12Hours(rawX),
                        y: y.invert(
                            event.clientY - containerRect.top - margin.top,
                        ),
                    });
                }
            });

        // Show highlight line if position is set
        if (selectionContext?.highlightPosition) {
            chart
                .append("line")
                .attr("class", "highlight-line")
                .attr("x1", xScale(selectionContext.highlightPosition.date))
                .attr("x2", xScale(selectionContext.highlightPosition.date))
                .attr("y1", 0)
                .attr("y2", height)
                .attr("stroke", "#3b82f6")
                .attr("stroke-width", 1)
                .style("stroke-dasharray", "4,4")
                .style("pointer-events", "none");
        }

        const legend = chart
            .append("g")
            .attr("class", "legend")
            .attr("transform", `translate(0, ${-5})`);

        // Always use the same order for legend (sorted by emission ID) to match color scale
        const legendData = [...processedData].sort((a, b) =>
            String(a.emissionId).localeCompare(String(b.emissionId)),
        );

        let xOffset = 0;
        legendData.forEach((alertingData) => {
            const color = colorScale(alertingData.emissionId);
            const legendItem = legend
                .append("g")
                .attr("transform", `translate(${xOffset}, 0)`);

            legendItem
                .append("rect")
                .attr("width", 12)
                .attr("height", 12)
                .attr("fill", color)
                .attr("fill-opacity", 1)
                .attr("stroke", color)
                .attr("stroke-width", 1);

            const label = `Emission ID: ${alertingData.emissionId}`;
            legendItem
                .append("text")
                .attr("x", 18)
                .attr("y", 10)
                .attr("font-size", "12px")
                .attr("fill", "#333")
                .text(label);

            xOffset += 18 + label.length * 7 + 15;
        });

        // Zoom handle
        addZoomBrushHandle({
            chart,
            height,
            width,
            xScale,
            yScale: y,
            constrainYToZero: true,
        });
    }, [
        data,
        dimensions,
        dataDateRange,
        selectionContext,
        setHoverContext,
        processedData,
        setClickContext,
        clickContext,
        addZoomBrushHandle,
        zoomDomain,
        sumPlots,
    ]);

    return (
        <div
            ref={containerRef}
            className="w-full h-64 relative"
            onMouseMove={(e) =>
                setCursorPosition({ x: e.clientX, y: e.clientY })
            }
            onMouseLeave={() => setCursorPosition(null)}
        >
            <svg ref={svgRef} style={{ width: "100%", height: "100%" }} />
            {/* Reset zoom button - only show when zoomed or date range selected */}
            {(currentTransform.k !== 1 ||
                currentTransform.x !== 0 ||
                currentTransform.y !== 0 ||
                zoomDomain.x !== null ||
                zoomDomain.y !== null ||
                selectionContext?.dateRange) && (
                <button
                    type="button"
                    onClick={() => {
                        resetZoom();
                    }}
                    className="absolute top-2 right-2 px-3 py-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 transition-colors text-sm"
                    title="Reset zoom"
                >
                    Reset Zoom
                </button>
            )}
        </div>
    );
};
